import { useCallback, useEffect, useRef } from "react";
import { log } from "@connect/observability";
import { createAuthenticatedPublicClient } from "@connect/data/public/client";
import type { components } from "@connect/data/public/schema";
import { classifyKycError } from "./classify-kyc-error";
import type {
  AddressConfirmationKycFailure,
  AddressKycFailure,
  ClassifiedKycError,
  TerminalKycFailure,
} from "./classify-kyc-error.types";
import { customerApiHeaders } from "./customer-api-headers";
import { draftToPatchBody } from "./draft-to-patch-body";
import type { KycDraft } from "./kyc-draft-context";
import { runWithTransientRetry } from "./retry-backoff";

type Customer = components["schemas"]["Customer"];
type Requirements = components["schemas"]["Requirements"];
type UpdateIdentityRequestBody = components["schemas"]["UpdateIdentityRequestBody"];

export interface UseSubmitKycParams {
  customerId: string | null | undefined;
  apiBaseUrl: string;
  clientHeaders: Record<string, string>;
  draft: KycDraft;
  requirements: Requirements | undefined;
  enabled: boolean;
  onSuccess?: (customer: Customer) => void;
  onNothingToSubmit?: () => void | Promise<void>;
  onTerminal?: (error: TerminalKycFailure) => void;
  onAddressError?: (error: AddressKycFailure | AddressConfirmationKycFailure) => void;
  onRetryableError?: (error: ClassifiedKycError) => void;
  retryWait?: (delayMs: number) => Promise<void>;
}

async function preparePatchCustomerKycRequest(
  customerId: string,
  apiBaseUrl: string,
  clientHeaders: Record<string, string>,
  body: UpdateIdentityRequestBody,
) {
  const client = createAuthenticatedPublicClient({
    baseUrl: apiBaseUrl,
    headers: await customerApiHeaders(clientHeaders),
  });
  return () =>
    client.PATCH("/platform/v1/customers/{id}/kyc", {
      params: { path: { id: customerId } },
      body,
    });
}

async function patchCustomerKycAttempt(
  customerId: string,
  apiBaseUrl: string,
  clientHeaders: Record<string, string>,
  body: UpdateIdentityRequestBody,
) {
  let request: Awaited<ReturnType<typeof preparePatchCustomerKycRequest>>;
  try {
    request = await preparePatchCustomerKycRequest(customerId, apiBaseUrl, clientHeaders, body);
  } catch (caught) {
    return {
      ok: false as const,
      error: classifyKycError({ error: caught, source: "local" }),
    };
  }
  let result: Awaited<ReturnType<typeof request>>;
  try {
    result = await request();
  } catch (caught) {
    return {
      ok: false as const,
      error: classifyKycError({ error: caught, source: "transport" }),
    };
  }
  const { data, error: apiError, response } = result;
  const customer = data?.data;
  if (customer) {
    return { ok: true as const, customer };
  }
  const httpStatus = response?.status;
  const isHttpFailure = httpStatus !== undefined && httpStatus >= 400;
  return {
    ok: false as const,
    error: classifyKycError({
      error: apiError ?? (isHttpFailure ? undefined : new Error("kyc_submit_failed")),
      httpStatus,
      source: apiError === undefined && !isHttpFailure ? "local" : "api",
    }),
  };
}

interface SubmitKycPatchParams {
  customerId: string;
  apiBaseUrl: string;
  clientHeaders: Record<string, string>;
  body: UpdateIdentityRequestBody;
  retryWait?: (delayMs: number) => Promise<void>;
}

export type SubmitKycPatchResult =
  | { ok: true; customer: Customer }
  | { ok: false; error: ClassifiedKycError };

async function attemptKycPatch({
  customerId,
  apiBaseUrl,
  clientHeaders,
  body,
  retryWait,
}: SubmitKycPatchParams): Promise<SubmitKycPatchResult> {
  try {
    return await runWithTransientRetry(
      () => patchCustomerKycAttempt(customerId, apiBaseUrl, clientHeaders, body),
      (attempt) => !attempt.ok && attempt.error.kind === "transient",
      retryWait,
    );
  } catch (caught) {
    return { ok: false, error: classifyKycError({ error: caught, source: "local" }) };
  }
}

export async function submitKycPatch(params: SubmitKycPatchParams): Promise<SubmitKycPatchResult> {
  const result = await attemptKycPatch(params);

  if (!result.ok && result.error.kind !== "state_changed") {
    log.warn("connect.onboarding.kyc_patch_failed", {
      reason: result.error.code ?? result.error.kind,
      ...("field" in result.error && { field: result.error.field }),
    });
  }

  return result;
}

export function useSubmitKyc({
  customerId,
  apiBaseUrl,
  clientHeaders,
  draft,
  requirements,
  enabled,
  onSuccess,
  onNothingToSubmit,
  onTerminal,
  onAddressError,
  onRetryableError,
  retryWait,
}: UseSubmitKycParams) {
  const draftRef = useRef(draft);
  const requirementsRef = useRef(requirements);
  const onSuccessRef = useRef(onSuccess);
  const onNothingToSubmitRef = useRef(onNothingToSubmit);
  const onTerminalRef = useRef(onTerminal);
  const onAddressErrorRef = useRef(onAddressError);
  const onRetryableErrorRef = useRef(onRetryableError);
  const requestRef = useRef({ apiBaseUrl, clientHeaders, retryWait });
  const submittedRef = useRef(false);

  useEffect(() => {
    draftRef.current = draft;
    requirementsRef.current = requirements;
    onSuccessRef.current = onSuccess;
    onNothingToSubmitRef.current = onNothingToSubmit;
    onTerminalRef.current = onTerminal;
    onAddressErrorRef.current = onAddressError;
    onRetryableErrorRef.current = onRetryableError;
    requestRef.current = { apiBaseUrl, clientHeaders, retryWait };
  });

  const submit = useCallback(async () => {
    if (!customerId) {
      return;
    }
    const latestRequirements = requirementsRef.current;
    if (latestRequirements === undefined) {
      return;
    }
    const body = draftToPatchBody(draftRef.current, latestRequirements);
    if (Object.keys(body).length === 0) {
      await onNothingToSubmitRef.current?.();
      return;
    }
    const result = await submitKycPatch({ customerId, body, ...requestRef.current });

    if (result.ok) {
      onSuccessRef.current?.(result.customer);

      return;
    }

    if (result.error.kind === "state_changed") {
      await onNothingToSubmitRef.current?.();

      return;
    }

    if (result.error.kind === "terminal") {
      onTerminalRef.current?.(result.error);
    } else if (result.error.kind === "address" || result.error.kind === "address_confirmation") {
      onAddressErrorRef.current?.(result.error);
    } else {
      onRetryableErrorRef.current?.(result.error);
    }
  }, [customerId]);

  useEffect(() => {
    if (!enabled || !customerId || requirements === undefined) {
      return;
    }
    if (submittedRef.current) {
      return;
    }
    submittedRef.current = true;
    void submit();
  }, [customerId, enabled, requirements, submit]);
}
